import { css } from '@emotion/react';
import type { ButtonProps } from '@mui/material';
import { Button, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { TpSkeletonText } from '@noah-labs/fe-shared-ui-shared';
import { useClickToCopy } from '../hooks/useClickToCopy';
import { TextOrSkeleton } from '../typography/TextOrSkeleton';
import { TruncatedText } from '../typography/TruncatedText';
import { DynamicCopyIcon } from './DynamicCopyIcon';

export type PpAddress = Pick<ButtonProps, 'sx' | 'variant'> & {
  /* IMPORTANT: DO NOT MANIPULATE THE ADDRESS - e.g. ADDRESSES ARE CASE SENSITIVE */
  address: TpSkeletonText;
  noWrap?: boolean;
  onCopy?: () => void;
  truncate?: boolean;
};

export function AddressWithCopy({
  address,
  truncate = true,
  noWrap = truncate,
  onCopy,
  sx,
  variant = 'contained',
}: PpAddress): React.ReactElement {
  const theme = useTheme();
  const styles = {
    button: css`
      text-align: left;
      justify-content: flex-start;
      min-width: 6em;
      max-width: 100%;
      text-transform: none !important;
    `,
    buttonText: css`
      box-sizing: content-box;
      margin: ${theme.spacing(-1)};
      padding: ${theme.spacing(1)};
      min-height: auto;
    `,
    textWrap: css`
      overflow: auto;
      word-wrap: break-word;
    `,
  };

  const buttonStyles = [styles.button];
  let buttonColor: ButtonProps['color'] = 'input';
  let fullWidth = true;

  if (variant === 'text') {
    buttonStyles.push(styles.buttonText);
    buttonColor = 'inherit';
    fullWidth = false;
  }

  const { copied, copyHandler } = useClickToCopy({ onCopy, text: address });

  return (
    <Button
      color={buttonColor}
      css={buttonStyles}
      data-qa="copy-button"
      data-qa-address={address}
      disabled={!address}
      fullWidth={fullWidth}
      startIcon={<DynamicCopyIcon copied={copied} />}
      sx={sx}
      variant={variant}
      onClick={copyHandler}
    >
      <Typography
        css={!noWrap && styles.textWrap}
        noWrap={noWrap}
        variant={variant === 'contained' ? 'paragraphBodyM' : 'paragraphBodyS'}
      >
        <TextOrSkeleton sx={{ px: '6em' }}>
          {address && truncate ? <TruncatedText prefix={12} text={address.toString()} /> : address}
        </TextOrSkeleton>
      </Typography>
    </Button>
  );
}
